import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Button,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { authService } from '../../services/authService';
import { useAuthStore } from '../../store/authStore';
import { PageHeader, LoadingState, StatusPill, PersonAvatar, FormSection, FieldGrid } from '../../components/ui';
import { tokens } from '../../theme';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Administrator systemu',
  SALON_OWNER: 'Właściciel',
  MANAGER: 'Menedżer',
  STAFF: 'Pracownik',
  ACCOUNTANT: 'Księgowy',
  ANALYST: 'Analityk',
  MARKETER: 'Marketer',
  IT: 'Informatyk',
  CLIENT: 'Klient',
};

export const SalonAccountPage: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  // Profile form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const profile = await authService.getProfile();
        setFirstName(profile.firstName || '');
        setLastName(profile.lastName || '');
        setPhone(profile.phone || '');
        setEmail(profile.email || '');
        setRole(profile.role || '');
      } catch {
        setLoadFailed(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const updated = await authService.updateProfile({ firstName, lastName, phone, email });
      if (user) {
        setUser({ ...user, email: updated.email, firstName: updated.firstName || undefined, lastName: updated.lastName || undefined });
      }
      setToast({ msg: 'Dane konta zapisane', kind: 'success' });
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Nie udało się zapisać danych';
      setToast({ msg, kind: 'error' });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async () => {
    if (newPassword !== confirmPassword) {
      setToast({ msg: 'Nowe hasła nie są zgodne', kind: 'error' });
      return;
    }
    setSavingPassword(true);
    try {
      await authService.changePassword({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setToast({ msg: 'Hasło zostało zmienione', kind: 'success' });
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Nie udało się zmienić hasła';
      setToast({ msg, kind: 'error' });
    } finally {
      setSavingPassword(false);
    }
  };

  const header = (
    <PageHeader
      title="Moje konto"
      description="Twoje dane kontaktowe i hasło do logowania."
    />
  );

  if (loading) {
    return (
      <Box>
        {header}
        <LoadingState variant="block" height={360} />
      </Box>
    );
  }

  const passwordValid = newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword);
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const visibilityToggle = (
    <InputAdornment position="end">
      <Tooltip title={showPasswords ? 'Ukryj hasła' : 'Pokaż hasła'}>
        <IconButton size="small" onClick={() => setShowPasswords((s) => !s)} edge="end" aria-label={showPasswords ? 'Ukryj hasła' : 'Pokaż hasła'}>
          {showPasswords ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );
  // Gdy profil sie nie wczytal, karta pokazuje przynajmniej dane z sesji.
  const shownFirst = firstName || (loadFailed ? user?.firstName : '') || '';
  const shownLast = lastName || (loadFailed ? user?.lastName : '') || '';
  const shownEmail = email || (loadFailed ? user?.email : '') || '';
  const shownRole = role || (loadFailed ? user?.role : '') || '';
  const displayName = [shownFirst, shownLast].filter(Boolean).join(' ');

  return (
    <Box>
      {header}

      {loadFailed && (
        <Alert
          severity="error"
          sx={{ mb: 2.5 }}
          action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Odśwież</Button>}
        >
          Nie udało się wczytać danych konta. Odśwież stronę, aby spróbować ponownie.
        </Alert>
      )}

      <Paper sx={{ px: { xs: 2, md: 2.5 }, py: 2, mb: 2.5 }}>
        <Stack direction="row" alignItems="center" gap={1.75} sx={{ minWidth: 0 }}>
          <PersonAvatar firstName={shownFirst} lastName={shownLast} fallback={shownEmail} seed={user?.id} size={48} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.0625rem', overflowWrap: 'anywhere' }}>{displayName || shownEmail}</Typography>
            {displayName && (
              <Typography sx={{ fontSize: '0.875rem', color: tokens.muted, overflowWrap: 'anywhere' }}>{shownEmail}</Typography>
            )}
          </Box>
          {shownRole && <StatusPill label={ROLE_LABELS[shownRole] || shownRole} tone="plum" noDot />}
        </Stack>
      </Paper>

      <Paper sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2.5, md: 3 }, pb: { xs: 1, md: 1.5 } }}>
        <FormSection first title="Dane profilu" description="Adres e-mail służy do logowania i powiadomień z Visitio.">
          <FieldGrid columns={2}>
            <TextField label="Imię" fullWidth value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
            <TextField label="Nazwisko" fullWidth value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
            <TextField label="Telefon" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
            <TextField
              label="Adres e-mail"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              error={!loadFailed && !email}
              helperText={!loadFailed && !email ? 'Podaj adres e-mail — bez niego nie zapiszesz zmian.' : undefined}
            />
          </FieldGrid>
          <Box sx={{ mt: 2.5 }}>
            <Button variant="contained" onClick={saveProfile} disabled={savingProfile || !email}>
              {savingProfile ? 'Zapisywanie…' : 'Zapisz dane'}
            </Button>
          </Box>
        </FormSection>

        <FormSection title="Zmiana hasła" description="Aby ustawić nowe hasło, podaj najpierw aktualne.">
          <Stack gap={2} sx={{ maxWidth: { sm: 420 } }}>
            <TextField
              label="Aktualne hasło"
              type={showPasswords ? 'text' : 'password'}
              fullWidth
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              InputProps={{ endAdornment: visibilityToggle }}
            />
            <TextField
              label="Nowe hasło"
              type={showPasswords ? 'text' : 'password'}
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              error={newPassword.length > 0 && !passwordValid}
              helperText="Min. 8 znaków, w tym 1 wielka litera i 1 cyfra."
            />
            <TextField
              label="Powtórz nowe hasło"
              type={showPasswords ? 'text' : 'password'}
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              error={mismatch}
              helperText={mismatch ? 'Hasła nie są takie same.' : undefined}
            />
          </Stack>
          <Box sx={{ mt: 2.5 }}>
            <Button
              variant="outlined"
              onClick={savePassword}
              disabled={savingPassword || !currentPassword || !passwordValid || newPassword !== confirmPassword}
            >
              {savingPassword ? 'Zmienianie…' : 'Zmień hasło'}
            </Button>
          </Box>
        </FormSection>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? (
          <Alert severity={toast.kind} onClose={() => setToast(null)} variant="filled">
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default SalonAccountPage;
